import { createAsyncThunk } from "@reduxjs/toolkit";

import logger from "Libs/logger";
import { AsyncThunkOptionType } from "Reducers/types";
import { addEnvironmentVariableRequest } from "Api/variables/environment/addEnvironmentVariable.request";
import { pickFromFields } from "Api/utils";
import { creatableEnvironmentVariableFields } from "Api/variables/environment/constants";
import { updateEnvironmentVariableRequest } from "Api/variables/environment/updateEnvironmentVariable.request";
import type { Environment } from "@packages/client";
import type { Variable } from "Api/variables/environment/types";

type CreateVariableParams = {
  fields: ReturnType<typeof pickFromFields>;
  projectId: string;
  environmentId: string;
};

type CreateVariableResponseType =
  | ReturnType<typeof updateEnvironmentVariableRequest>
  | ReturnType<typeof addEnvironmentVariableRequest>;

export const createVariable = async ({
  fields,
  projectId,
  environmentId
}: CreateVariableParams): Promise<CreateVariableResponseType | undefined> => {
  return await addEnvironmentVariableRequest({
    data: fields,
    projectId,
    environmentId
  });
};

type EnvironmentVariableData = {
  id: string;
  name: string;
  value: string;
  is_json: boolean;
  is_enabled: boolean;
  is_inheritable: boolean;
  is_sensitive: boolean;
  visible_build: boolean;
  visible_runtime: boolean;
  application_scope: string[] | undefined;
};

type AddEnvironmentVariableThunkPropType = {
  environment: Environment;
  environmentId: string;
  projectId: string;
  organizationId: string;
  data: EnvironmentVariableData;
};

export const addVariable = createAsyncThunk<
  Variable | undefined,
  AddEnvironmentVariableThunkPropType,
  AsyncThunkOptionType<{ error: string }>
>(
  "app/project/environment/variable/add",
  async (
    { data, environment, projectId, environmentId },
    { rejectWithValue }
  ) => {
    try {
      const fields = pickFromFields(
        {
          name: data.name,
          value: data.value,
          is_json: data.is_json,
          is_enabled: data.is_enabled,
          is_inheritable: data.is_inheritable,
          is_sensitive: data.is_sensitive,
          visible_build: data.visible_build,
          visible_runtime: data.visible_runtime,
          application_scope: data.application_scope
        },
        [...creatableEnvironmentVariableFields]
      );

      const result = await createVariable({
        fields,
        projectId,
        environmentId
      });

      const variable = result?._embedded?.entity;

      if ((result && result?.code / 100) === 4) {
        return rejectWithValue({ error: JSON.stringify(result) });
      }

      return variable;
    } catch (err: any) {
      logger(err, {
        environment,
        action: "environmentAddVariable"
      });
      return rejectWithValue({ error: err?.detail?.name || err.message });
    }
  },
  {
    condition: ({ environment }) => {
      return !!environment?._links?.["#manage-variables"]?.href;
    }
  }
);
